import dayjs from 'dayjs';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { CALCULATED_AFTER_MODAL_SAVED_PLACEHOLDER } from 'constants/loanApplication';
import {
  branchOfServiceOptions,
  entitlementCodeFromCoeOptions,
  militaryStatusOptions,
} from 'constants/options';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { BorrowersTestIds } from '../../../../../../Borrowers.testIds';
import { defaultRadioInputProps, firstTimeVaUseOptions } from '../Declarations.constants';
import {
  getCheckboxStyles,
  getTextFieldStyles,
  sectionStyles,
  sectionTitleStyles,
  textFieldStyles,
} from '../Declarations.styles';

export const getModelPath = entityId => `[${entityId}].declarations.militaryService.`;

const minCurrentlyServingDate = dayjs().startOf('day').add(1, 'day');

export const getConfigFields = ({
  entityId,
  isServedInMilitary,
  isCurrentlyServing,
  areFieldsRequired,
  onFieldChange,
  isBasicEntitlementWillBeCalculatedAfterSave,
  isBonusEntitlementWillBeCalculatedAfterSave,
  basicEntitlementManualUpdate,
  bonusEntitlementManualUpdate,
}) => {
  const modelPath = getModelPath(entityId);

  return {
    isServedInMilitary: {
      fieldId: 'isServedInMilitary',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}isServedInMilitary`,
      inputProps: {
        label:
          'Did you (or your deceased spouse) ever serve, or are you currently serving, in the United States Armed Forces?',
        dataTestId: BorrowersTestIds.MILITARY_SERVICES_FORM_IS_SERVED_IN_MILITARY_SELECT,
        'data-test-value': 'true',
        ...defaultRadioInputProps,
      },
    },
    isCurrentlyServing: {
      fieldId: 'isCurrentlyServing',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}isCurrentlyServing`,
      inputProps: {
        customOnChange: onFieldChange,
        label: 'Currently serving on active duty with projected expiration date of service/tour',
        customStyles: getCheckboxStyles(),
        disabled: !isServedInMilitary,
        required: areFieldsRequired,
        'data-testid': BorrowersTestIds.MILITARY_SERVICES_FORM_IS_CURRENTLY_SERVING_CHECKBOX,
        'data-test-value': 'true',
      },
    },
    currentlyServingDate: {
      fieldId: 'currentlyServingDate',
      type: FieldType.DATE,
      nameInModel: `${modelPath}currentlyServingDate`,
      inputProps: {
        label: 'Date',
        customStyles: textFieldStyles,
        required: isCurrentlyServing,
        variant: 'outlined',
        format: COMMON_DATE_FORMAT,
        dataTestId: BorrowersTestIds.MILITARY_SERVICES_FORM_CURRENTLY_SERVING_DATE_INPUT,
        containerClassName: 'mb-8',
        disabled: !isCurrentlyServing,
        minDate: minCurrentlyServingDate,
      },
    },
    isCurrentlyRetired: {
      fieldId: 'isCurrentlyRetired',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}isCurrentlyRetired`,
      inputProps: {
        customOnChange: onFieldChange,
        label: 'Currently retired, discharged, or separated from service',
        customStyles: getCheckboxStyles(),
        disabled: !isServedInMilitary,
        required: areFieldsRequired,
        'data-testid': BorrowersTestIds.MILITARY_SERVICES_FORM_IS_CURRENTLY_RETIRED_CHECKBOX,
        'data-test-value': 'true',
      },
    },
    isNonActivatedMemberOfReserveNationalGuard: {
      fieldId: 'isNonActivatedMemberOfReserveNationalGuard',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}isNonActivatedMemberOfReserveNationalGuard`,
      inputProps: {
        customOnChange: onFieldChange,
        label:
          'Only period of service was as a non-activated member of the Reserve or National Guard',
        customStyles: getCheckboxStyles(),
        disabled: !isServedInMilitary,
        required: areFieldsRequired,
        'data-testid':
          BorrowersTestIds.MILITARY_SERVICES_FORM_IS_NON_ACTIVATED_MEMBER_OF_RESERVE_NATIONAL_GUARD_CHECKBOX,
      },
    },
    isSurvivingSpouse: {
      fieldId: 'isSurvivingSpouse',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}isSurvivingSpouse`,
      inputProps: {
        customOnChange: onFieldChange,
        label: 'Surviving spouse',
        customStyles: getCheckboxStyles(),
        disabled: !isServedInMilitary,
        required: areFieldsRequired,
        'data-testid': BorrowersTestIds.MILITARY_SERVICES_FORM_IS_SURVIVING_SPOUSE_CHECKBOX,
      },
    },
    entitlementCodeFromCoe: {
      fieldId: 'entitlementCodeFromCoe',
      type: FieldType.SELECT,
      nameInModel: `${modelPath}entitlementCodeFromCoe`,
      inputProps: {
        label: 'Entitlement code from COE',
        customStyles: getTextFieldStyles(),
        required: areFieldsRequired,
        disabled: !isServedInMilitary,
        options: entitlementCodeFromCoeOptions,
        customOnChange: onFieldChange,
      },
    },
    branchOfService: {
      fieldId: 'branchOfService',
      type: FieldType.SELECT,
      nameInModel: `${modelPath}branchOfService`,
      inputProps: {
        label: 'Branch of Service',
        customStyles: getTextFieldStyles(),
        required: areFieldsRequired,
        disabled: !isServedInMilitary,
        options: branchOfServiceOptions,
        customOnChange: onFieldChange,
      },
    },
    militaryStatus: {
      fieldId: 'militaryStatus',
      type: FieldType.SELECT,
      nameInModel: `${modelPath}militaryStatus`,
      inputProps: {
        label: 'Status',
        customStyles: getTextFieldStyles({ withHelperText: true }),
        required: areFieldsRequired,
        disabled: !isServedInMilitary,
        options: militaryStatusOptions,
        withSortedOptions: false,
        customOnChange: onFieldChange,
      },
    },
    basicEntitlementManualUpdate: {
      fieldId: 'basicEntitlement',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}basicEntitlementManualUpdate`,
      inputProps: {
        label: 'Manual',
        customStyles: getCheckboxStyles(),
        containerClassName: 'mt-[30px]',
        disabled: !isServedInMilitary,
        required: false,
      },
    },
    basicEntitlement: {
      fieldId: 'basicEntitlement',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}basicEntitlement`,
      inputProps: {
        type: FieldInputType.CURRENCY,
        label: 'Basic Entitlement',
        placeholder: isBasicEntitlementWillBeCalculatedAfterSave
          ? CALCULATED_AFTER_MODAL_SAVED_PLACEHOLDER
          : 'Basic Entitlement',
        customStyles: textFieldStyles,
        required: false,
        minValue: 0,
        disabled:
          !isServedInMilitary ||
          !basicEntitlementManualUpdate ||
          isBasicEntitlementWillBeCalculatedAfterSave,
      },
    },
    bonusEntitlementManualUpdate: {
      fieldId: 'bonusEntitlement',
      type: FieldType.CHECKBOX,
      nameInModel: `${modelPath}bonusEntitlementManualUpdate`,
      inputProps: {
        label: 'Manual',
        containerClassName: 'mt-[30px]',
        customStyles: getCheckboxStyles(),
        disabled: !isServedInMilitary,
        required: false,
      },
    },
    bonusEntitlement: {
      fieldId: 'bonusEntitlement',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}bonusEntitlement`,
      inputProps: {
        type: FieldInputType.CURRENCY,
        label: 'Bonus Entitlement',
        placeholder: isBonusEntitlementWillBeCalculatedAfterSave
          ? CALCULATED_AFTER_MODAL_SAVED_PLACEHOLDER
          : 'Bonus Entitlement',
        customStyles: textFieldStyles,
        required: false,
        minValue: 0,
        disabled:
          !isServedInMilitary ||
          !bonusEntitlementManualUpdate ||
          isBonusEntitlementWillBeCalculatedAfterSave,
      },
    },
    totalEntitlementAmount: {
      fieldId: 'totalEntitlementAmount',
      type: FieldType.INPUT,
      nameInModel: `${modelPath}totalEntitlementAmount`,
      inputProps: {
        type: FieldInputType.CURRENCY,
        label: 'Total Entitlement Amount',
        placeholder:
          isBonusEntitlementWillBeCalculatedAfterSave || isBasicEntitlementWillBeCalculatedAfterSave
            ? CALCULATED_AFTER_MODAL_SAVED_PLACEHOLDER
            : 'Total Entitlement Amount',
        customStyles: textFieldStyles,
        required: false,
        minValue: 0,
        disabled: true,
        columns: 2,
      },
    },
    firstTimeVaUse: {
      fieldId: 'firstTimeVaUse',
      type: FieldType.SELECT,
      nameInModel: `${modelPath}firstTimeVaUse`,
      inputProps: {
        label: 'First Time VA Use',
        customStyles: getTextFieldStyles(),
        required: false,
        disabled: !isServedInMilitary,
        options: firstTimeVaUseOptions,
        customOnChange: onFieldChange,
      },
    },
    isExemptVAFundingFee: {
      fieldId: 'isExemptVAFundingFee',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}isExemptVAFundingFee`,
      inputProps: {
        ...defaultRadioInputProps,
        disabled: !isServedInMilitary,
        label: 'Exempt from VA Funding Fee',
        dataTestId: BorrowersTestIds.MILITARY_SERVICES_FORM_IS_EXEMPT_VA_FUNDING_FEE_SELECT,
        'data-test-value': 'true',
      },
    },
  };
};

export const getConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    isServedInMilitary,
    isCurrentlyServing,
    areFieldsRequired,
    entityId,
    onFieldChange,
    isBasicEntitlementWillBeCalculatedAfterSave,
    isBonusEntitlementWillBeCalculatedAfterSave,
    basicEntitlementManualUpdate,
    bonusEntitlementManualUpdate,
  }) => {
    const militaryConfigFields = getConfigFields({
      entityId,
      isServedInMilitary,
      isCurrentlyServing,
      areFieldsRequired,
      onFieldChange,
      isBasicEntitlementWillBeCalculatedAfterSave,
      isBonusEntitlementWillBeCalculatedAfterSave,
      basicEntitlementManualUpdate,
      bonusEntitlementManualUpdate,
    });

    return {
      groups: [
        {
          title: { label: 'Military Services', titleStyles: sectionTitleStyles },
          key: `${entityId}-declarations-military-service`,
          customStyles: sectionStyles,
          type: FieldType.GROUP,
          fields: [
            militaryConfigFields.isServedInMilitary,
            ...getConditionalArray(isServedInMilitary, [
              militaryConfigFields.isCurrentlyServing,
              militaryConfigFields.currentlyServingDate,
              militaryConfigFields.isCurrentlyRetired,
              militaryConfigFields.isNonActivatedMemberOfReserveNationalGuard,
              militaryConfigFields.isSurvivingSpouse,
              militaryConfigFields.entitlementCodeFromCoe,
              militaryConfigFields.branchOfService,
              militaryConfigFields.militaryStatus,
              {
                type: FieldType.GROUP,
                customStyles: {
                  gridTemplateColumns: 'repeat(2, 1fr)',
                },
                fields: [
                  militaryConfigFields.basicEntitlement,
                  militaryConfigFields.basicEntitlementManualUpdate,
                  militaryConfigFields.bonusEntitlement,
                  militaryConfigFields.bonusEntitlementManualUpdate,
                  militaryConfigFields.totalEntitlementAmount,
                ],
              },
              militaryConfigFields.firstTimeVaUse,
              militaryConfigFields.isExemptVAFundingFee,
            ]),
          ],
        },
      ],
    };
  },
);
